// Left column: orders list with search, filters, and infinite scroll.
import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Search, Loader2, AlertTriangle, CreditCard, Wallet, ShoppingBag,
  CalendarDays, Truck, X,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "today", label: "Astăzi", icon: CalendarDays },
  { id: "week", label: "7 zile" },
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Plătite" },
  { id: "fulfilled", label: "Expediate", icon: Truck },
  { id: "unfulfilled", label: "Neexpediate" },
  { id: "cancelled", label: "Anulate" },
  { id: "cod", label: "Ramburs", icon: Wallet },
  { id: "card", label: "Card", icon: CreditCard },
  { id: "risk", label: "Risc", icon: AlertTriangle },
];

export default function OrdersList({
  orders, loading, loadingMore, activeOrderId, onOpen,
  search, setSearch, filter, setFilter, onLoadMore, hasMore,
}) {
  const listRef = useRef(null);

  // Infinite scroll
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!hasMore || loadingMore) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 200) onLoadMore?.();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    <>
      {/* Sticky search */}
      <div className="px-3 py-2 border-b border-border space-y-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută nume, telefon, email, comandă, AWB..."
            className="pl-8 pr-7 h-9 text-sm bg-secondary/40 border-border"
            data-testid="orders-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="orders-search-clear"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`orders-filter-${f.id}`}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${
                filter === f.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f.icon ? <f.icon className="h-3 w-3" /> : null}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto scrollbar-thin" data-testid="orders-list">
        {loading ? (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-md bg-secondary/30 animate-pulse" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 px-6 text-sm text-muted-foreground">
            <ShoppingBag className="h-6 w-6 mx-auto mb-2 opacity-40" />
            Nicio comandă găsită.
          </div>
        ) : (
          <>
            {orders.map((o) => (
              <OrderRow
                key={o.id || o.shopify_id}
                o={o}
                active={(o.id || o.shopify_id) === activeOrderId}
                onClick={() => onOpen(o.id || o.shopify_id)}
              />
            ))}
            {hasMore && (
              <div className="py-4 text-center text-xs text-muted-foreground">
                {loadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin inline" />
                ) : (
                  <button onClick={onLoadMore} className="hover:text-foreground">
                    Încarcă mai multe
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function OrderRow({ o, active, onClick }) {
  const initials = (o.customer_name || o.email || o.phone || "?")
    .split(/\s|@/).filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const fin = statusInfo(o.financial_status, "financial");
  const ful = statusInfo(o.fulfillment_status, "fulfillment");
  const isCancelled = !!o.cancelled_at;
  const isHighRisk = o.risk_level === "high";

  return (
    <button
      onClick={onClick}
      data-testid={`order-row-${o.shopify_id}`}
      className={`w-full text-left px-3 py-3 border-b border-border/50 transition-all flex gap-3 items-start ${
        active
          ? "bg-primary/8 border-l-2 border-l-primary"
          : "hover:bg-secondary/40 border-l-2 border-l-transparent"
      }`}
    >
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display font-semibold text-sm truncate">{o.name}</div>
          <div className="font-mono text-sm font-semibold tabular-nums shrink-0">
            {Number(o.total).toFixed(0)} {o.currency}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-xs text-muted-foreground truncate">
            {o.customer_name || "—"}
          </div>
          <div className="text-[10px] text-muted-foreground shrink-0">
            {o.shopify_created_at ? fmtRelative(o.shopify_created_at) : ""}
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground/80 font-mono truncate">
          {o.phone || o.email || "—"}
        </div>
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          {isCancelled ? (
            <Badge className="text-[9px] h-4 px-1.5 py-0 bg-red-500/10 text-red-600 border-red-500/30 hover:bg-red-500/10">
              Anulată
            </Badge>
          ) : (
            <>
              <StatusPill {...fin} />
              <StatusPill {...ful} />
            </>
          )}
          {isHighRisk && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 py-0 border-red-500/30 text-red-500">
              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
              risc
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function StatusPill({ label, color }) {
  if (!label) return null;
  return (
    <Badge
      variant="outline"
      className="text-[9px] h-4 px-1.5 py-0 border-current/30 capitalize"
      style={{ color, background: color + "12", borderColor: color + "30" }}
    >
      {label}
    </Badge>
  );
}

function statusInfo(s, kind) {
  if (!s) return { label: kind === "fulfillment" ? "neexpediată" : null, color: "#71717a" };
  const m = String(s).toLowerCase();
  if (m === "paid" || m === "partially_paid") return { label: "plătită", color: "#10b981" };
  if (m === "pending") return { label: "pending", color: "#f59e0b" };
  if (m === "refunded" || m === "partially_refunded") return { label: "refund", color: "#3b82f6" };
  if (m === "voided") return { label: "voided", color: "#71717a" };
  if (m === "fulfilled") return { label: "expediată", color: "#10b981" };
  if (m === "unfulfilled") return { label: "neexpediată", color: "#f59e0b" };
  if (m === "partial") return { label: "parțial", color: "#f59e0b" };
  return { label: m, color: "#71717a" };
}
